/**
 * Position feature: controls how the object is framed inside the export by
 * padding the subject's bounding box. Padding is a percentage per side and is
 * fed into the shared bbox-framing math in batchEditService (same values drive
 * the baked export and the live card preview, so they stay in lock-step).
 *
 * Modes:
 *  - center:   equal padding on every side (single slider value).
 *  - custom:   independent padding per side (four inputs), offsetting the subject.
 *  - original: no reframing — the full image keeps its original framing.
 */

export type BatchPositionMode = 'center' | 'custom' | 'original'

/** Sides of the padding box. */
export type PaddingSide = 'top' | 'right' | 'bottom' | 'left'

export interface PositionConfig {
  mode: BatchPositionMode
  /** Padding percentages (0–POSITION_PADDING_MAX) per side. */
  top: number
  right: number
  bottom: number
  left: number
}

/** Per-side padding as fractions (0..1) of the target edge — what the bbox math consumes. */
export interface PositionPadding {
  top: number
  right: number
  bottom: number
  left: number
}

export const POSITION_PADDING_MIN = 0
/** Capped below 50 so opposing sides can never consume the whole axis. */
export const POSITION_PADDING_MAX = 45
/** Default equal padding used by Center mode (matches the legacy BBOX_PADDING_RATIO of 0.1). */
export const POSITION_CENTER_DEFAULT = 10

/** Sides in the 2×2 custom-grid display order (Left, Top / Right, Bottom). */
export const PADDING_SIDES: PaddingSide[] = ['left', 'top', 'right', 'bottom']

/** A fresh Center config at the default 10% padding. */
export function defaultPosition(): PositionConfig {
  return {
    mode: 'center',
    top: POSITION_CENTER_DEFAULT,
    right: POSITION_CENTER_DEFAULT,
    bottom: POSITION_CENTER_DEFAULT,
    left: POSITION_CENTER_DEFAULT,
  }
}

/** Absent position => the Center 10% default (the feature's default state). */
export function resolvePosition(pos: PositionConfig | null | undefined): PositionConfig {
  return pos ?? defaultPosition()
}

/** A fresh Original config (no padding) — the default when the background isn't removed. */
export function defaultOriginalPosition(): PositionConfig {
  return { mode: 'original', top: 0, right: 0, bottom: 0, left: 0 }
}

/**
 * Cutout and original framing are independent preferences, kept in separate
 * fields (`cutoutPosition` / `fullImagePosition`) so toggling bg-removal restores each
 * mode's own last choice instead of clobbering one with the other. Absent =>
 * Center 10% for the cutout, Original (no padding) for the full image — each
 * mode's own natural default.
 */
export function resolveEffectivePosition(image: {
  bgRemoved: boolean
  cutoutPosition?: PositionConfig | null
  fullImagePosition?: PositionConfig | null
}): PositionConfig {
  return image.bgRemoved ? resolvePosition(image.cutoutPosition) : image.fullImagePosition ?? defaultOriginalPosition()
}

const clampPct = (v: number): number =>
  Math.max(POSITION_PADDING_MIN, Math.min(POSITION_PADDING_MAX, Number.isFinite(v) ? v : 0))

/** Symmetric padding (all sides equal) built from a single fraction. */
export function symmetricPadding(ratio: number): PositionPadding {
  return { top: ratio, right: ratio, bottom: ratio, left: ratio }
}

/** Per-side padding fractions for a config. Original => zero (framing is disabled elsewhere). */
export function positionPaddingRatios(pos: PositionConfig | null | undefined): PositionPadding {
  const p = resolvePosition(pos)
  if (p.mode === 'original') return { top: 0, right: 0, bottom: 0, left: 0 }
  return {
    top: clampPct(p.top) / 100,
    right: clampPct(p.right) / 100,
    bottom: clampPct(p.bottom) / 100,
    left: clampPct(p.left) / 100,
  }
}

/** Whether a config reframes the subject (Center/Custom) vs. leaving the full image (Original). */
export function positionReframes(pos: PositionConfig | null | undefined): boolean {
  return resolvePosition(pos).mode !== 'original'
}
